import React, { useState } from 'react';
import { Plus, Trash2, Upload, Edit3, Save, Star } from 'lucide-react';
import api, { formatApiError } from '../../lib/api';
import { useData } from '../../context/DataContext';
import { useToast } from '../../hooks/use-toast';

const emptyProduct = {
  name_en: '',
  name_hi: '',
  description_en: '',
  description_hi: '',
  price: 499,
  originalPrice: 999,
  image: '',
  category: 'mala',
  purposes: [],
  stock: 50,
  rating: 4.5,
  ratingCount: 0,
  featured: false,
};

const CATEGORIES = [
  { v: 'mala', l: 'Mala' },
  { v: 'yantra', l: 'Yantra' },
  { v: 'idol', l: 'Idol' },
  { v: 'bracelet', l: 'Bracelet' },
  { v: 'other', l: 'Other' },
];

const PURPOSES = ['wealth', 'health', 'love', 'protection', 'courage', 'knowledge'];

export default function ProductsTab() {
  const { products, refresh } = useData();
  const { toast } = useToast();
  const [form, setForm] = useState(emptyProduct);
  const [editingId, setEditingId] = useState(null);
  const [show, setShow] = useState(false);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyProduct);
    setShow(true);
    window.scrollTo({ top: 300, behavior: 'smooth' });
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({ ...emptyProduct, ...p, purposes: p.purposes || [] });
    setShow(true);
    window.scrollTo({ top: 300, behavior: 'smooth' });
  };

  const del = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await api.delete(`/api/products/${id}`);
      await refresh();
      toast({ title: 'Product deleted' });
    } catch (e) {
      toast({ title: formatApiError(e), variant: 'destructive' });
    }
  };

  const uploadFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => setForm((prev) => ({ ...prev, image: r.result }));
    r.readAsDataURL(f);
  };

  const togglePurpose = (p) => {
    setForm((prev) => {
      const has = prev.purposes.includes(p);
      return { ...prev, purposes: has ? prev.purposes.filter((x) => x !== p) : [...prev.purposes, p] };
    });
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      price: +form.price || 0,
      originalPrice: +form.originalPrice || 0,
      stock: +form.stock || 0,
      rating: +form.rating || 0,
      ratingCount: +form.ratingCount || 0,
    };
    try {
      if (editingId) await api.put(`/api/products/${editingId}`, payload);
      else await api.post('/api/products', payload);
      await refresh();
      toast({ title: editingId ? 'Product updated' : 'Product added' });
      setShow(false);
    } catch (err) {
      toast({ title: formatApiError(err), variant: 'destructive' });
    }
  };

  return (
    <>
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <p className="text-sm text-nv-muted">
          Manage divine products — add, edit price/discount, stock, category & purposes
        </p>
        <button
          data-testid="add-product-btn"
          onClick={openNew}
          className="btn-orange rounded-full px-4 md:px-5 py-2.5 font-semibold flex items-center gap-2 text-sm"
        >
          <Plus className="w-4 h-4" /> Add Product
        </button>
      </div>

      {show && (
        <form
          onSubmit={save}
          className="bg-white border border-orange-200 rounded-2xl p-4 md:p-6 mb-6 grid md:grid-cols-2 gap-4 shadow-md"
        >
          <h3 className="md:col-span-2 text-lg font-bold text-orange-800 flex items-center gap-2">
            {editingId ? <Edit3 className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
            {editingId ? 'Edit Product' : 'New Product'}
          </h3>

          {[
            ['name_en', 'Name (English)', true],
            ['name_hi', 'Name (Hindi)', true],
          ].map(([k, l, req]) => (
            <div key={k}>
              <label className="text-xs font-medium text-stone-600 mb-1 block">{l}</label>
              <input
                value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                required={req}
                className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          ))}

          <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-3 bg-orange-50/70 border border-orange-200 rounded-xl p-3">
            <div>
              <label className="text-xs font-semibold text-orange-800 mb-1 block">Price ₹</label>
              <input
                type="number"
                min="1"
                value={form.price}
                onChange={(e) => setForm({ ...form, price: e.target.value })}
                required
                className="w-full border border-orange-300 rounded-lg px-3 py-2 text-sm bg-white font-semibold text-orange-700"
                data-testid="product-price-input"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-orange-800 mb-1 block">Original ₹</label>
              <input
                type="number"
                min="0"
                value={form.originalPrice}
                onChange={(e) => setForm({ ...form, originalPrice: e.target.value })}
                className="w-full border border-orange-300 rounded-lg px-3 py-2 text-sm bg-white"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-orange-800 mb-1 block">Stock</label>
              <input
                type="number"
                min="0"
                value={form.stock}
                onChange={(e) => setForm({ ...form, stock: e.target.value })}
                className="w-full border border-orange-300 rounded-lg px-3 py-2 text-sm bg-white"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-orange-800 mb-1 flex items-center gap-1">
                <Star className="w-3 h-3" /> Rating
              </label>
              <input
                type="number"
                step="0.1"
                min="0"
                max="5"
                value={form.rating}
                onChange={(e) => setForm({ ...form, rating: e.target.value })}
                className="w-full border border-orange-300 rounded-lg px-3 py-2 text-sm bg-white"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 block">Category</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm bg-white"
              data-testid="product-category-select"
            >
              {CATEGORIES.map((c) => (
                <option key={c.v} value={c.v}>
                  {c.l}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-stone-600 mb-1 block">Rating count</label>
            <input
              type="number"
              min="0"
              value={form.ratingCount}
              onChange={(e) => setForm({ ...form, ratingCount: e.target.value })}
              className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-xs font-medium text-stone-600 mb-1 block">Purposes (select any)</label>
            <div className="flex flex-wrap gap-2">
              {PURPOSES.map((p) => {
                const active = form.purposes.includes(p);
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => togglePurpose(p)}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold capitalize border transition ${
                      active
                        ? 'border-orange-500 bg-orange-500 text-white'
                        : 'border-orange-200 bg-white text-orange-700 hover:border-orange-400'
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="text-xs font-medium text-stone-600 mb-1 block">Description (English)</label>
            <textarea
              value={form.description_en}
              onChange={(e) => setForm({ ...form, description_en: e.target.value })}
              className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm"
              rows="2"
            />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-medium text-stone-600 mb-1 block">विवरण (हिन्दी)</label>
            <textarea
              value={form.description_hi}
              onChange={(e) => setForm({ ...form, description_hi: e.target.value })}
              className="w-full border border-orange-200 rounded-lg px-3 py-2 text-sm"
              rows="2"
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-xs font-medium text-stone-600 mb-1 block">Image (URL or upload)</label>
            <div className="flex gap-2">
              <input
                value={form.image}
                onChange={(e) => setForm({ ...form, image: e.target.value })}
                placeholder="https://..."
                className="flex-1 border border-orange-200 rounded-lg px-3 py-2 text-sm"
              />
              <label className="px-3 py-2 rounded-lg border border-orange-300 text-orange-700 text-sm cursor-pointer hover:bg-orange-50 flex items-center gap-1">
                <Upload className="w-4 h-4" /> Upload
                <input type="file" accept="image/*" onChange={uploadFile} className="hidden" />
              </label>
            </div>
            {form.image && <img src={form.image} alt="" className="mt-2 h-32 rounded-lg object-cover" />}
          </div>

          <label className="md:col-span-2 flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form.featured}
              onChange={(e) => setForm({ ...form, featured: e.target.checked })}
            />
            Mark as featured
          </label>

          <div className="md:col-span-2 flex flex-wrap gap-3 justify-end pt-3">
            <button
              type="button"
              onClick={() => setShow(false)}
              className="px-5 py-2 rounded-lg border border-stone-300 text-stone-700"
            >
              Cancel
            </button>
            <button
              data-testid="save-product-btn"
              type="submit"
              className="btn-orange px-6 py-2 rounded-lg font-semibold flex items-center gap-2"
            >
              <Save className="w-4 h-4" /> Save Product
            </button>
          </div>
        </form>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="products-list">
        {products.map((p) => {
          const discount =
            p.originalPrice > p.price ? Math.round(((p.originalPrice - p.price) / p.originalPrice) * 100) : 0;
          return (
            <div
              key={p.id}
              className="bg-white border border-nv-gold/20 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition"
            >
              <div className="relative">
                <img src={p.image} alt="" className="w-full h-32 object-cover bg-orange-50" />
                {discount > 0 && (
                  <span className="absolute top-2 left-2 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                    {discount}% OFF
                  </span>
                )}
              </div>
              <div className="p-4">
                <h4 className="font-semibold text-sm text-nv-wine line-clamp-2 mb-1">{p.name_en}</h4>
                <div className="flex items-center gap-2 text-xs text-nv-muted mb-1 capitalize">
                  {p.category} • Stock: {p.stock ?? 0}
                </div>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-orange-700 font-bold text-base">₹ {p.price}</span>
                  {p.originalPrice > p.price && (
                    <span className="text-xs text-stone-400 line-through">₹{p.originalPrice}</span>
                  )}
                </div>
                {p.purposes?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {p.purposes.map((pp) => (
                      <span
                        key={pp}
                        className="text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full capitalize"
                      >
                        {pp}
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex gap-2">
                  <button
                    data-testid={`edit-product-${p.id}`}
                    onClick={() => openEdit(p)}
                    className="flex-1 border border-orange-300 text-orange-700 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1 hover:bg-orange-50"
                  >
                    <Edit3 className="w-3 h-3" /> Edit
                  </button>
                  <button
                    data-testid={`delete-product-${p.id}`}
                    onClick={() => del(p.id)}
                    className="flex-1 border border-red-300 text-red-600 py-1.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-1 hover:bg-red-50"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}
